/**
 * /clinical-assessment — tier-aware paid entry.
 *
 *  WITHOUT a tier+token  → render the public TierPicker (Square checkout links).
 *  WITH a tier+token     → render the KCIS Clinical Deep Dive™ — a distinct,
 *                          premium investigatory experience. NEVER mounts the
 *                          free Wellness Gateway / legacy adaptive engine.
 *
 * The legacy `<Assessment />` engine continues to serve the FREE Wellness
 * Gateway under `/assessment`. This route is the paid lane.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import ClinicalDeepDive from "@/pages/assessment/ClinicalDeepDive";
import {
  assessmentPurchaseApi,
  persistTierContext,
  readTierContext,
} from "@/lib/assessmentPurchaseApi";


export default function ClinicalAssessment() {
  const [params] = useSearchParams();
  const cachedCtx = useMemo(() => readTierContext(), []);

  const tier = useMemo(() => {
    const fromQuery = (params.get("tier") || "").toLowerCase();
    return fromQuery || (cachedCtx?.tier || "");
  }, [params, cachedCtx]);

  const purchaseToken = useMemo(() => {
    return (params.get("ref") || cachedCtx?.purchase_token || "").trim();
  }, [params, cachedCtx]);

  // Persist context so a mid-flow refresh continues seamlessly.
  useEffect(() => {
    if (!tier) return;
    const next = {
      ...(cachedCtx || {}),
      tier,
      purchase_token: purchaseToken || cachedCtx?.purchase_token || "",
    };
    persistTierContext(next);
  }, [tier, purchaseToken, cachedCtx]);

  // Fire the "started" ping the first time we land with a token.
  useEffect(() => {
    if (!purchaseToken) return;
    assessmentPurchaseApi.start(purchaseToken, {}).catch(() => { /* tolerate */ });
  }, [purchaseToken]);

  if (!tier || !purchaseToken) {
    return <TierPicker missingToken={Boolean(tier && !purchaseToken)} />;
  }

  return (
    <div data-testid="clinical-assessment-shell">
      <ClinicalDeepDive tier={tier} purchaseToken={purchaseToken} />
    </div>
  );
}


function TierPicker({ missingToken = false }) {
  const [tiers, setTiers] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    assessmentPurchaseApi.tiers()
      .then((r) => { if (!cancelled) setTiers(r.data.tiers || []); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Failed to load tiers"); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/60 via-white to-white" data-testid="clinical-assessment-tier-picker">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KCIS Clinical Assessment</span>
          </Link>
        </div>
      </header>

      <section className="px-6 py-12 text-center border-b border-slate-200 bg-gradient-to-br from-purple-100/40 via-white to-amber-50/30">
        <span className="inline-block rounded-full border border-purple-300 bg-white px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-purple-900 font-semibold">
          Choose your tier
        </span>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl text-slate-900 leading-tight">
          KCIS Clinical Deep Dive™
        </h1>
        <p className="mt-3 text-sm text-slate-700 max-w-xl mx-auto leading-relaxed">
          A premium investigatory experience. Three tiers of depth. Each purchase unlocks a tier-specific clinical dossier reviewed by the KISSED team.
        </p>
        {missingToken ? (
          <div className="mt-5 inline-block rounded-2xl border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="missing-token-warning">
            We couldn&rsquo;t verify your purchase. Please complete checkout, or return through the email link we sent.
          </div>
        ) : null}
      </section>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {err && (
          <p className="text-sm text-red-600 mb-4" data-testid="tp-error">{err}</p>
        )}
        {!tiers ? (
          <p className="text-sm text-slate-500" data-testid="tp-loading">Loading tiers…</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <TierCard key={t.key} tier={t} />
            ))}
          </div>
        )}

        <p className="mt-10 text-xs text-slate-500 text-center" data-testid="tp-footnote">
          After payment, you&rsquo;ll be redirected to <strong>/assessment-purchased</strong> where you can begin the Clinical Deep Dive™ immediately.
        </p>

        <div
          className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 text-center"
          data-testid="tp-in-person-block"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-2">
            Prefer an in-person service?
          </div>
          <p className="text-sm text-slate-600 leading-relaxed max-w-xl mx-auto">
            Hair &amp; scalp analysis, wellness scans, and new client evaluations
            are also available in person.
          </p>
          <a
            href="https://book.squareup.com/appointments/037am6afc9z7rn/location/2WZDAQVBX8ADX/services"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-4"
            data-testid="tp-book-in-person"
          >
            <Button
              variant="outline"
              className="rounded-full border-slate-400 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900"
            >
              Book an In-Person Service
            </Button>
          </a>
          <div className="mt-2 text-[11px] text-slate-400" data-testid="tp-in-person-subtext">
            Opens in Square Appointments
          </div>
        </div>
      </main>
    </div>
  );
}

// (removed inline state shim — using React's useState directly above)

function TierCard({ tier }) {
  const accent = tier.key === "premium"
    ? "ring-2 ring-amber-300 border-amber-200 bg-amber-50/30"
    : tier.key === "standard"
      ? "border-purple-200 bg-white"
      : "border-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border ${accent} p-6 shadow-sm flex flex-col`} data-testid={`tier-card-${tier.key}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold">
        {tier.label}
      </div>
      <div className="font-display text-3xl text-slate-900 mt-2" data-testid={`tier-price-${tier.key}`}>
        {tier.price_display}
      </div>
      <div className="mt-1 text-sm text-slate-700">{tier.tagline}</div>
      <p className="mt-3 text-sm text-slate-700 leading-relaxed">{tier.summary}</p>

      <ul className="mt-4 space-y-1.5 text-sm text-slate-700 flex-1">
        {(tier.includes || []).map((line) => (
          <li key={line} className="flex items-start gap-2">
            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-purple-700 shrink-0" />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <a
        href={tier.checkout_url}
        rel="noopener noreferrer"
        className="mt-6"
        data-testid={`tier-buy-${tier.key}`}
      >
        <Button
          size="lg"
          className={`w-full rounded-full ${tier.key === "premium" ? "bg-amber-500 hover:bg-amber-600 text-slate-900" : "bg-purple-900 hover:bg-purple-800 text-white"}`}
        >
          Buy {tier.label}
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
      <p className="mt-2 text-[11px] text-slate-500 text-center">Secure Square checkout · returns to KCIS</p>
    </div>
  );
}
